import React, { useState } from 'react';
import { useSearchContacts } from '../../hooks/use-search-contacts';
import { AlertTriangleIcon, RefreshCwIcon, SidebarCloseIcon, SidebarOpenIcon } from 'lucide-react';

import { Button } from '@@/ui/primitives';

import { Spinner } from '@@/ui/components';
import { ContactGroups } from './contact-groups';
import { SearchContactsItem } from '../../../api/api-client';
import { ContactInitiationMethod, SearchContactsTimeRangeType } from '@aws-sdk/client-connect';
import { ContactSearch, getDefaultTimeline } from './search';
import { AttributesFilter } from './search/filter-attributes';
import { useLocalStorage } from '@@/hooks/use-local-storage';
import clsx from 'clsx';

export const ContactList = (props: {
  onContactSelect: (contact: SearchContactsItem) => void;
  selectedContactId: string | null;
}) => {
  const [includePending, setIncludePending] = useLocalStorage<boolean>('includePending', false);
  const [selectedInitiationMethods, setSelectedInitiationMethods] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['VOICE', 'CHAT']);
  const [selectedQueueIds, setSelectedQueueIds] = useState<string[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [contactId, setContactId] = useState('');
  const [timeline, setTimeline] = useState<{
    startTime: string;
    endTime: string;
    type: SearchContactsTimeRangeType;
  }>(getDefaultTimeline());
  const [segmentAttributes, setSegmentAttributes] = useState<AttributesFilter>({
    criteria: [],
    matchType: 'MATCH_ALL'
  });
  const [contactAttributes, setContactAttributes] = useState<AttributesFilter>({
    criteria: [],
    matchType: 'MATCH_ALL'
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { data, isLoading, isRefetching, error, refetch } = useSearchContacts({
    agentIds: contactId.trim() ? undefined : selectedAgentIds,
    queueIds: contactId.trim() ? undefined : selectedQueueIds,
    contactId: contactId.trim() ? contactId.trim() : undefined,
    channels: contactId.trim() ? undefined : selectedChannels,
    initiationMethods: contactId.trim() ? undefined : (selectedInitiationMethods as ContactInitiationMethod[]),
    includePending: includePending ?? false,
    timeRange: timeline,
    segmentAttributes: contactId.trim() || segmentAttributes.criteria.length === 0 ? undefined : segmentAttributes,
    contactAttributes: contactId.trim() || contactAttributes.criteria.length === 0 ? undefined : contactAttributes
  });

  return (
    <div
      className={clsx(
        'bg-white h-full flex flex-col z-10',
        // 'border-neutral-200 border rounded-xl m-2 mr-0',
        'border-r border-neutral-200',
        !sidebarOpen && 'w-12',
        sidebarOpen && 'w-80',
        'transition-all duration-200 overflow-hidden'
      )}
    >
      <div className='px-1.5 flex items-center gap-1 h-12 min-h-12 text-sidebar-foreground'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className='text-sidebar-foreground/60'
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {sidebarOpen ? <SidebarCloseIcon /> : <SidebarOpenIcon />}
        </Button>
        {sidebarOpen && (
          <>
            <h3 className='text-sm font-medium text-nowrap mb-[0.5px]'>Insights</h3>
            <div className='flex items-center gap-0.5 ml-auto'>
              {/* <Button
                variant='ghost'
                size='icon'
                className='rounded-full'
                aria-label='Include pending contacts'
                onClick={() => setIncludePending(!includePending)}
                title='Include pending contacts'
              >
                {includePending ? (
                  <CircleDotDashedIcon className={`size-4 text-green-800`} />
                ) : (
                  <CircleDashedIcon className={`size-4 text-neutral-400`} />
                )}
              </Button> */}

              <Button
                variant='ghost'
                size='icon'
                onClick={() => refetch()}
                className='rounded-full'
                disabled={isRefetching}
                aria-label='Refresh contacts'
              >
                <RefreshCwIcon className={`size-4 ${isRefetching && 'animate-spin'}`} />
              </Button>
            </div>
          </>
        )}
      </div>
      {sidebarOpen && (
        <>
          <ContactSearch
            contactId={contactId}
            onContactIdChange={setContactId}
            selectedAgentIds={selectedAgentIds}
            onAgentIdsChange={setSelectedAgentIds}
            selectedQueueIds={selectedQueueIds}
            onQueueIdsChange={setSelectedQueueIds}
            selectedInitiationMethods={selectedInitiationMethods}
            onInitiationMethodsChange={setSelectedInitiationMethods}
            selectedChannels={selectedChannels}
            onChannelsChange={setSelectedChannels}
            includePending={includePending ?? false}
            onIncludePendingChange={setIncludePending}
            timeline={timeline}
            onTimelineChange={setTimeline}
            segmentAttributes={segmentAttributes}
            onSegmentAttributesChange={setSegmentAttributes}
            contactAttributes={contactAttributes}
            onContactAttributesChange={setContactAttributes}
          />

          {isLoading && (
            <div className='p-4 flex justify-center'>
              <Spinner className='stroke-1' />
            </div>
          )}
          {error && !isLoading && (
            <div className='py-4 px-4 bg-red-50 mx-2 rounded-lg'>
              <AlertTriangleIcon className='size-4 text-red-950 inline-block' />
              <p className='text-red-950 text-sm font-medium mt-1'>Sorry. There was an error.</p>
              <pre className='text-red-950 text-sm! overflow-auto mt-2 font-mono'>{error.message}</pre>
            </div>
          )}
          {!isLoading && data && (
            <ContactGroups
              contacts={data.contacts}
              totalCount={data.totalCount}
              onContactSelect={props.onContactSelect}
              selectedContactId={props.selectedContactId}
            />
          )}
        </>
      )}
    </div>
  );
};
